import React, { useContext } from 'react';
import { Toolbar, IconButton, Link as MuiLink, Menu, AppBar, Button, MenuItem, Avatar, ListItemIcon, Box, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import { Link, useNavigate } from "react-router-dom";
import PersonAdd from '@mui/icons-material/PersonAdd';
import Build from '@mui/icons-material/Build';
import Logout from '@mui/icons-material/Logout';
import ConstructionIcon from '@mui/icons-material/Construction';
import { useTranslation } from "react-i18next";
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { UserContext } from '../userContext';
import bounLogo from "../boun_logo.png";

export default function Navigation({ handleDrawerToggle }) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useContext(UserContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleLogOut = () => {
    logout();
    navigate("/");
  };

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("lang", lang);
  };

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: '#ffffff',
        color: '#003366',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        borderBottom: '1px solid #e0e0e0'
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: '64px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <MuiLink 
            href="https://www.cmpe.bogazici.edu.tr/" 
            target="_blank"
            sx={{ display: 'flex', alignItems: 'center', mr: 2 }}
          >
            <img alt="boun logo" src={bounLogo} style={{ height: '40px' }} />
          </MuiLink>

          <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 800, 
                fontSize: isMobile ? '1.1rem' : '1.25rem',
                letterSpacing: '-0.02em'
              }}
            >
              {isMobile ? 'TULAP' : 'TULAP Platform'}
            </Typography>
          </Link>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Link to="/about" style={{ textDecoration: 'none' }}>
            <Button color="inherit" sx={{ fontWeight: 600 }}>{t('about')}</Button>
          </Link>

          <Box sx={{ display: 'flex', alignItems: 'center', borderLeft: '1px solid #e0e0e0', ml: 1, pl: 1 }}>
            <Button 
              size="small"
              onClick={() => changeLanguage(i18n.language === 'en' ? 'tr' : 'en')}
              sx={{ minWidth: '40px', fontWeight: 700 }}
            >
              {i18n.language.toUpperCase()}
            </Button>
          </Box>

          <IconButton
            onClick={handleMenuOpen}
            sx={{ ml: 1 }}
          >
            {user.auth ? (
              <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32, fontSize: '0.9rem' }}>
                {user.username[0].toUpperCase()}
              </Avatar>
            ) : (
              <Link to="/login" style={{ color: 'inherit' }}>
                <AccountCircle fontSize="large" />
              </Link>
            )}
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {user.auth && (
              <Box>
                <MenuItem component={Link} to="/manageTools" onClick={handleMenuClose}>
                  <ListItemIcon><ConstructionIcon fontSize="small" /></ListItemIcon>
                  {user.user_type === "administrator" ? t("managetools") : t("mytools")}
                </MenuItem>
                <MenuItem component={Link} to="/AddTool" onClick={handleMenuClose}>
                  <ListItemIcon><Build fontSize="small" /></ListItemIcon>
                  {t("addtool")}
                </MenuItem>
                {user.user_type === "administrator" && (
                  <MenuItem component={Link} to="/manageUsers" onClick={handleMenuClose}>
                    <ListItemIcon><PersonAdd fontSize="small" /></ListItemIcon>
                    {t("manageusers")}
                  </MenuItem>
                )}
                <MenuItem onClick={handleLogOut}>
                  <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
                  {t("logout")}
                </MenuItem>
              </Box>
            )}
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
